// PromptEditorDrawer.tsx (FULL — always renders Markdown; scrollable body + pinned footer)

import React from "react";
import { useTranslation } from "react-i18next";
import { Play, Save as SaveIcon, X } from "lucide-react";
import { styles, btnIcon, btnIconFilled, btnIconGhost } from "./styles";
import { Kind } from "./types";
import { renderMarkdown } from "./Markdown";

type Props = {
  visible: boolean;
  close: () => void;

  editName: string;
  setEditName: (s: string) => void;

  editKind: Kind | string;
  setEditKind: (k: Kind | string) => void;

  editDescription: string;
  setEditDescription: (s: string) => void;

  editTemplate: string;
  setEditTemplate: (s: string) => void;

  // typed extractor fields (visible when kind === 'trend')
  editSource: string;
  setEditSource: (s: string) => void;
  editOutputType: string;
  setEditOutputType: (s: string) => void;
  editOuraField: string;
  setEditOuraField: (s: string) => void;
  editTaxonomy: string;
  setEditTaxonomy: (s: string) => void;
  editScaleAnchors: string;
  setEditScaleAnchors: (s: string) => void;
  editAirtimeThreshold: string;
  setEditAirtimeThreshold: (s: string) => void;

  resultText: string;

  onRun: () => void;
  onSave: () => void;

  /** True when this summarize prompt is the one the session summarizer runs. */
  isSummarizer?: boolean;
  /** Make (or unmake) this prompt the session summarizer. Present only for
   * kind='summarize' prompts when the parent wires preferences through. */
  onSetSummarizer?: (use: boolean) => void;
};

const PromptEditorDrawer: React.FC<Props> = (p) => {
  const { t } = useTranslation();
  if (!p.visible) return null;

  return (
    <>
      <div style={styles.backdrop} onClick={p.close} />
      <div style={styles.drawer} role="dialog" aria-modal="true">
        {/* Header (fixed) */}
        <div style={styles.drawerHeader}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{t("prompts.editor.title")}</div>
          <button
            style={btnIconGhost()}
            onClick={p.close}
            title={t("common.close")}
            aria-label={t("common.close")}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body (scrolls) */}
        <div style={styles.drawerBody}>
          {/* Top row: name + kind */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 160px",
              gap: 12,
            }}
          >
            <input
              value={p.editName}
              onChange={(e) => p.setEditName(e.target.value)}
              placeholder={t("prompts.editor.name")}
              style={styles.input}
            />

            <select
              value={p.editKind}
              onChange={(e) => p.setEditKind(e.target.value as Kind)}
              style={styles.input}
              title={t("prompts.editor.kind")}
            >
              <option value="tool">tool</option>
              <option value="trend">trend</option>
            </select>
          </div>

          {/* Summarizer picker — the session's Notes tab runs exactly one
              summarize prompt; this is where it's chosen. Styled to match the
              calendar's editorial callouts (.cal-invoice): dashed strong
              border, subsurface, mono uppercase micro-label. */}
          {p.editKind === "summarize" && p.onSetSummarizer && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                margin: "14px 0",
                padding: "10px 12px",
                border: "1px dashed var(--color-border-strong)",
                borderRadius: "var(--radius-sm)",
                background: "var(--color-subsurface)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={!!p.isSummarizer}
                onChange={(e) => p.onSetSummarizer!(e.target.checked)}
              />
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--color-ink)",
                  whiteSpace: "nowrap",
                }}
              >
                {t("prompts.editor.useAsSummarizer")}
              </span>
              <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                {t("prompts.editor.useAsSummarizerHint")}
              </span>
            </label>
          )}

          {/* Description */}
          <div>
            <div style={styles.label}>{t("prompts.editor.description")}</div>
            <textarea
              value={p.editDescription}
              onChange={(e) => p.setEditDescription(e.target.value)}
              rows={3}
              style={{ ...styles.input, lineHeight: 1.45 }}
              placeholder={t("prompts.editor.descriptionPlaceholder")}
            />
          </div>

          {/* Template */}
          <div>
            <div style={styles.label}>
              {t("prompts.editor.template")}{" "}
              <span style={{ fontWeight: 400, color: "var(--color-muted)" }}>
                {t("prompts.editor.templateHint")}
              </span>
            </div>
            <textarea
              value={p.editTemplate}
              onChange={(e) => p.setEditTemplate(e.target.value)}
              rows={12}
              style={{
                ...styles.input,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                lineHeight: 1.5,
              }}
              placeholder={t("prompts.editor.templatePlaceholder")}
            />
          </div>

          {/* Extractor settings — only for trend prompts */}
          {p.editKind === "trend" && (
            <div
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: 14,
                background: "var(--color-subsurface)",
                display: "grid",
                gap: 12,
              }}
            >
              <div style={{ ...styles.label, marginBottom: 0 }}>
                {t("prompts.extractor.title")}
                <span style={{ fontWeight: 400, color: "var(--color-muted)", marginLeft: 6 }}>
                  {t("prompts.extractor.subtitle")}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <div style={styles.label}>{t("prompts.extractor.source")}</div>
                  <select
                    value={p.editSource}
                    onChange={(e) => p.setEditSource(e.target.value)}
                    style={styles.input}
                  >
                    <option value="">{t("prompts.extractor.sourceAuto")}</option>
                    <option value="session">session</option>
                    <option value="oura">oura</option>
                    <option value="goal">goal</option>
                    <option value="assessment">assessment</option>
                  </select>
                </div>
                <div>
                  <div style={styles.label}>{t("prompts.extractor.outputType")}</div>
                  <select
                    value={p.editOutputType}
                    onChange={(e) => p.setEditOutputType(e.target.value)}
                    style={styles.input}
                  >
                    <option value="">{t("prompts.extractor.outputAuto")}</option>
                    <option value="score">{t("prompts.extractor.outputScore")}</option>
                    <option value="topics">{t("prompts.extractor.outputTopics")}</option>
                    <option value="airtime">{t("prompts.extractor.outputAirtime")}</option>
                    <option value="markers">{t("prompts.extractor.outputMarkers")}</option>
                  </select>
                </div>
                {p.editSource === "oura" && (
                  <div>
                    <div style={styles.label}>{t("prompts.extractor.ouraField")}</div>
                    <select
                      value={p.editOuraField}
                      onChange={(e) => p.setEditOuraField(e.target.value)}
                      style={styles.input}
                    >
                      <option value="">—</option>
                      <option value="HRV">HRV</option>
                      <option value="Sleep">Sleep</option>
                      <option value="Readiness">Readiness</option>
                      <option value="Pulse">Pulse</option>
                    </select>
                  </div>
                )}
                {p.editOutputType === "airtime" && (
                  <div>
                    <div style={styles.label}>{t("prompts.extractor.threshold")}</div>
                    <input
                      value={p.editAirtimeThreshold}
                      onChange={(e) => p.setEditAirtimeThreshold(e.target.value)}
                      placeholder="0.40"
                      inputMode="decimal"
                      style={styles.input}
                    />
                  </div>
                )}
              </div>

              {(p.editOutputType === "topics" || p.editOutputType === "airtime") && (
                <div>
                  <div style={styles.label}>
                    {t("prompts.extractor.taxonomy")}{" "}
                    <span style={{ fontWeight: 400, color: "var(--color-muted)" }}>
                      {t("prompts.extractor.taxonomyHint")}
                    </span>
                  </div>
                  <textarea
                    value={p.editTaxonomy}
                    onChange={(e) => p.setEditTaxonomy(e.target.value)}
                    rows={4}
                    style={{ ...styles.input, lineHeight: 1.5 }}
                    placeholder={t("prompts.extractor.taxonomyPlaceholder")}
                  />
                </div>
              )}

              {p.editSource !== "oura" && p.editSource !== "goal" && p.editOutputType !== "airtime" && (
                <div>
                  <div style={styles.label}>
                    {t("prompts.extractor.anchors")}{" "}
                    <span style={{ fontWeight: 400, color: "var(--color-muted)" }}>
                      {t("prompts.extractor.anchorsHint")}
                    </span>
                  </div>
                  <textarea
                    value={p.editScaleAnchors}
                    onChange={(e) => p.setEditScaleAnchors(e.target.value)}
                    rows={4}
                    style={{ ...styles.input, lineHeight: 1.5 }}
                    placeholder={t("prompts.extractor.anchorsPlaceholder")}
                  />
                </div>
              )}

              <div style={{ fontSize: 11.5, color: "var(--color-muted)", lineHeight: 1.5 }}>
                {t("prompts.extractor.versionNote")}
              </div>
            </div>
          )}

          {/* Result header */}
          <div style={styles.label}>{t("prompts.editor.result")}</div>

          {/* Result body (always Markdown) */}
          <div style={styles.resultBox}>
            <div
              className="mdPreview"
              style={styles.mdPreview}
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(p.resultText),
              }}
            />
          </div>
        </div>

        {/* Footer (pinned) */}
        <div style={styles.drawerFooter}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              style={btnIconFilled("var(--color-brand)", "#fff")}
              onClick={p.onRun}
              title={t("common.run")}
              aria-label={t("common.run")}
            >
              <Play size={16} />
            </button>
            <button
              style={btnIconFilled("var(--color-success)", "#fff")}
              onClick={p.onSave}
              title={t("common.save")}
              aria-label={t("common.save")}
            >
              <SaveIcon size={16} />
            </button>
            <button
              style={btnIcon()}
              onClick={p.close}
              title={t("common.close")}
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PromptEditorDrawer;
